import { z } from 'zod';
/**
 * Vocabulario de invalidación de caché.
 *
 * Vive en contracts porque lo comparten tres lados: la API (que purga sus lecturas públicas y
 * emite `Surrogate-Key` para el CDN), el server SSR del sitio (que purga páginas renderizadas)
 * y el CDN mismo. Un tag escrito distinto en dos lados es una purga que **nunca ocurre**, y el
 * síntoma —contenido viejo en el sitio— aparece mucho después y es dificilísimo de atribuir.
 */
/** Canal de pub/sub por el que la API avisa que hay que purgar. */
export const CACHE_INVALIDATION_CHANNEL = 'cache:invalidate';
/** Tags de alcance de página (no llevan id: hay uno solo por tenant). */
export const CACHE_TAG = {
    /** La portada. La toca cualquier cambio de bloques, orden, o de una nota publicada. */
    home: 'home',
    /** El sitemap: cambia al publicarse o borrarse una nota, o al tocar categorías. */
    sitemap: 'sitemap',
    /**
     * El cascarón que viaja en **toda** respuesta pública: marca del medio, menú de secciones y
     * redes. Lo lleva cada lectura cacheada porque un cambio de nombre de sección o de logo se
     * ve en la cabecera de todas las páginas: sin este tag, la portada se actualizaría y las
     * notas seguirían mostrando el menú viejo hasta que venza su propio TTL.
     */
    shell: 'shell',
    /**
     * Los avisos. Van en toda respuesta que renderiza una página —portada, nota, sección,
     * búsqueda y 404—, así que tocar una campaña las toca todas. Sin este tag, pausar un aviso
     * se vería en la portada y las notas seguirían mostrándolo hasta que venciera su TTL.
     */
    ads: 'ads',
};
/** Tag de una nota. La purga alcanza su página y cualquier listado que la incluya. */
export function articleTag(articleId) {
    return `article:${articleId}`;
}
/** Tag de una sección: su página paginada y los bloques de la home que la muestran. */
export function categoryTag(categoryId) {
    return `category:${categoryId}`;
}
/**
 * Normaliza un `Host` a la forma con la que se identifica un tenant: sin puerto y en
 * minúsculas (`AhoraPatagonia.com:443` → `ahorapatagonia.com`).
 *
 * Tiene que ser **la misma** regla que usa `TenantResolverMiddleware` en la API, porque el
 * caché del SSR particiona por host y el tenant se resuelve por host: si una punta conservara
 * el puerto y la otra no, `localhost:5174` y `localhost` serían dos particiones del mismo
 * tenant y la purga solo limpiaría una.
 */
export function normalizeHost(host) {
    return host.split(',')[0].trim().split(':')[0].toLowerCase();
}
/**
 * Cap de tags por mensaje. Publicar una nota emite su tag + uno por categoría + home +
 * sitemap, así que 100 sobra de lejos. El límite existe porque el mensaje se parsea de un
 * canal de Redis compartido: un array sin techo es una purga sin techo.
 */
export const CACHE_INVALIDATION_MAX_TAGS = 100;
/**
 * Lo que viaja por el canal.
 *
 * Lleva `domain` además de `tenantId` porque las dos cachés keyean distinto: la API por
 * `tenantId` (lo tiene a mano en cada request) y el SSR por `Host` (es lo único que conoce
 * antes de llamar a la API). Sin el dominio, el server del sitio no sabría qué páginas son
 * de este tenant.
 */
export const CacheInvalidationMessage = z
    .object({
    tenantId: z.string().uuid(),
    slug: z.string().min(1),
    /** Host del tenant; `null` cuando el medio todavía no tiene dominio propio (dev). */
    domain: z.string().min(1).nullable(),
    tags: z.array(z.string().min(1)).min(1).max(CACHE_INVALIDATION_MAX_TAGS),
})
    .strict();
